import { Text } from '@lmc/ui-kit/src/general'
import { Button } from '@lmc/ui-kit'
import { FC, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import Divider from '@lmc/ui-kit/src/layout/Divider/Divider'
import dayjs from 'dayjs'
import isoWeek from 'dayjs/plugin/isoWeek'
import relativeTime from 'dayjs/plugin/relativeTime'
import styled from 'styled-components'
import { getClockInWarnings, getClockOutWarnings } from './utils'
import { getQueryParams } from 'utils/getQueryParams'
import { ClockInDetails } from './ClockInDetails'
import { ClockOutDetails } from './ClockOutDetails'
import {
  ClockInOutAction,
  ClockInOutType,
} from 'screens/Rostering/hooks/useClockInOut'
import {
  ISO_STRING_FORMAT,
  getDateTimeInZone,
} from 'screens/Rostering/utils.datetime'
import useDurationWarnings from 'screens/Rostering/hooks/useDurationWarnings'
import { Warnings } from './Warnings'
import { Link } from 'react-router-dom'
import {
  RotaEventInstance,
  ClockInTeamMember,
  RotaInstanceStatus,
} from 'screens/Rostering/types'

dayjs.extend(isoWeek)
dayjs.extend(relativeTime)

type ClockInOutDetailsProps = {
  revisionDetail: RotaEventInstance
  teamMember: ClockInTeamMember
  openClockInOutModal: (args: {
    type: ClockInOutType
    action: ClockInOutAction
    teamMember: ClockInTeamMember
  }) => void
  publishedRota?: boolean
  isAddressInvalidForGeofencing?: boolean
}

export const ClockInOutDetails: FC<ClockInOutDetailsProps> = ({
  revisionDetail,
  teamMember,
  openClockInOutModal,
  publishedRota,
  isAddressInvalidForGeofencing,
}) => {
  const { teamMemberId } = teamMember

  const { t } = useTranslation()

  const clockInOutDetails = useMemo(
    () =>
      revisionDetail?.clockIns?.find(
        (clockIn: any) => clockIn.teamMemberId === teamMemberId,
      ),
    [revisionDetail, teamMemberId],
  )

  const clockedIn = clockInOutDetails?.clockedIn
  const clockInTime =
    clockInOutDetails?.clockInTime || clockInOutDetails?.clockInTimeSystem
  const clockInNote = clockInOutDetails?.clockInNotes
  const clockInLocation = clockInOutDetails?.clockInGeoLocation

  const clockedOut = clockInOutDetails?.clockedOut
  const clockOutTime =
    clockInOutDetails?.clockOutTime || clockInOutDetails?.clockOutTimeSystem
  const clockOutNote = clockInOutDetails?.clockOutNotes
  const clockOutLocation = clockInOutDetails?.clockOutGeoLocation

  const actualDuration = clockInOutDetails?.actualDurationFormatted
  const plannedDuration = revisionDetail?.expectedDuration
  const isDraft = revisionDetail.status === RotaInstanceStatus.DRAFT
  const isDeletedSU = !!revisionDetail?.deletedServiceUser

  const clockInWarnings: string[] = useMemo(
    () => getClockInWarnings(teamMember.warnings),
    [clockInOutDetails],
  )

  const clockOutWarnings: string[] = useMemo(
    () => getClockOutWarnings(teamMember.warnings),
    [clockInOutDetails],
  )

  const durationWarnings: string[] = useDurationWarnings(teamMember.warnings)

  const queryParams = useMemo(
    () =>
      getQueryParams({
        resident: revisionDetail?.resident?.id,
        loggedBy: teamMemberId,
        from: getDateTimeInZone(revisionDetail?.startTime).format(
          ISO_STRING_FORMAT,
        ),
        to: getDateTimeInZone(revisionDetail?.endTime).format(
          ISO_STRING_FORMAT,
        ),
        isPreciseDateTime: true,
      }),
    [revisionDetail, teamMemberId],
  )

  return (
    <>
      <ClockInDetails
        time={clockInTime}
        note={clockInNote}
        location={clockInLocation}
        warnings={clockInWarnings}
        action={clockedIn ? 'edit' : 'manual'}
        openClockInOutModal={() => {
          return openClockInOutModal({
            type: 'in',
            action: clockedIn ? 'edit' : 'manual',
            teamMember,
          })
        }}
        isDraft={isDraft}
        isDeletedSU={isDeletedSU}
        timezone={revisionDetail?.timezone}
        publishedRota={publishedRota}
        showInvalidAddressForGeofencingWarning={isAddressInvalidForGeofencing}
      />

      {clockedIn && (
        <ClockOutDetails
          time={clockOutTime}
          note={clockOutNote}
          location={clockOutLocation}
          warnings={clockOutWarnings}
          action={clockedOut ? 'edit' : 'manual'}
          openClockInOutModal={() => {
            return openClockInOutModal({
              type: 'out',
              action: clockedOut ? 'edit' : 'manual',
              teamMember,
            })
          }}
          isDeletedSU={isDeletedSU}
          timezone={revisionDetail?.timezone}
          publishedRota={publishedRota}
          showInvalidAddressForGeofencingWarning={isAddressInvalidForGeofencing}
        />
      )}

      <Divider />

      <DurationInfo>
        <DurationInfoSection>
          <Warnings warnings={durationWarnings}>
            <GrayText level={2}>{t('ACTUAL_DURATION')} </GrayText>
          </Warnings>
          <Text>{actualDuration ?? t('NOT_AVAILABLE')}</Text>
        </DurationInfoSection>

        <DurationInfoSection>
          <GrayText level={2}>{t('PLANNED_DURATION')} </GrayText>
          <Text>{plannedDuration ?? t('NOT_AVAILABLE')}</Text>
        </DurationInfoSection>
      </DurationInfo>

      {!isDeletedSU && (
        <>
          <Divider />
          <div>
            <Button
              variant="primary"
              as={Link}
              to={`/logs?${queryParams}`}
              target="_blank"
            >
              {t('VIEW_LOGS_CREATED')}
            </Button>
          </div>{' '}
        </>
      )}
    </>
  )
}

const GrayText = styled(Text)`
  color: ${(props) => props.theme.color.neutral.color5};
`
const DurationInfo = styled.div`
  display: flex;
  flex-direction: row;
  width: 100%;
`

const DurationInfoSection = styled.div`
  flex: 1 1 0;
  display: flex;
  flex-direction: column;
  padding: 16px;
`
